import { types, flow, onSnapshot, getParent } from "mobx-state-tree";
import * as constants from "../../constants/constants";
import AppConfig from "../../constants/config";
import { parseFields } from "../../stores/attributes/attributes.helpers";
import {
  flatten,
  uniq,
  intersection,
  sortWith,
  prop,
  ascend,
  descend,
  difference,
  equals,
  reduceRight,
  sortBy,
  compose,
} from "ramda";
import { showNaturalColor } from "../../api/layers";
import {
  setLayerFootprints,
  zoomToLayer,
  toggleVisibility,
} from "../../stores/layers/layers.helpers";

import { getSelectedIds, computeIds, getOtherIds } from "./helper";
import { configStore } from "../config/config";

const loadFeatures = async (config) => {
  const outFields = constants.ATTRIBUTES[configStore.name];
  const url = `${config.baseUrl}/query?where=1%3D1&objectIds=&time=&geometry=&geometryType=esriGeometryEnvelope&inSR=&spatialRel=esriSpatialRelIntersects&relationParam=&outFields=${outFields}&returnGeometry=false&outSR=&returnIdsOnly=false&returnCountOnly=false&pixelSize=&orderByFields=&groupByFieldsForStatistics=&outStatistics=&returnDistinctValues=true&multidimensionalDefinition=&returnTrueCurves=false&maxAllowableOffset=&geometryPrecision=&f=json`;

  return fetch(url)
    .then((response) => response.json())
    .then((response) => {
      const attributes = parseFields(response, configStore.name);
      const features = flatten(attributes.map((a) => a.features));

      return sortFeatures(features);
    })
    .catch((error) => error);
};

const getFeaturesOrdering = async (config) => {
  const url = config.orderingUrl;
  const collections = await fetch(url)
    .then((data) => data.json())
    .then((data) => data.collections);
  return collections;
};

const layerActions = {
  ["visible"]: {
    run: toggleVisibility,
  },
  ["zoomed"]: {
    run: zoomToLayer,
  },
  ["naturalColored"]: {
    run: showNaturalColor,
  },
  ["footprintEnabled"]: {
    run: setLayerFootprints,
  },
};

const sortById = sortWith([descend(prop("id"))]);

const Feature = types
  .model({
    constraint: types.number,
    disabled: false,
    fieldIndex: types.number,
    fieldName: types.string,
    footprintDisabled: true,
    footprintEnabled: false,
    hasNaturalColor: false,
    id: types.number,
    title: types.string,
    label: types.union(types.string, types.number),
    layerIds: types.array(types.number),
    naturalColored: false,
    selected: false,
    value: types.union(types.number, types.string),
    visible: false,
    zoom: types.number,
    zoomed: false,
  })
  .views((self) => ({
    hideFootprint: () => {
      const parent = getParent(self, 2);
      return false;
      // return parent.extentFeatureCount > 1000;
    },
  }))
  .actions((self) => ({
    toggle: (field) => {
      const parent = getParent(self, 2);
      parent.setCurrent(self);
      self[field] = !self[field];

      if (self.fieldName === constants.DATASET_ID) {
        if (self[field]) parent.addSelection(field, self.label);
        else parent.removeSelection(field, self.label);
      }

      if (field === "selected") {
        parent.disableOthers();
      } else if (parent.hasReachedMaxSelection(field)) return;
    },
    update: (field, value) => (self[field] = value),
    performAction: ({ field, enabled, imagery, view }) => {
      if (self.fieldName !== constants.DATASET_ID) return;
      const parent = getParent(self, 2);
      if (parent.hasReachedMaxSelection(field)) return;
      if (self.fieldName === constants.DATASET_ID) {
        if (self[field]) parent.addSelection(field, self.label);
        else parent.removeSelection(field, self.label);
      }
      const ids = parent.layerIdsByField(field);
      const extentFeatureCount = parent.extentFeatureCount;
      if (
        (field === "footprintEnabled" && self.hideFootprint()) ||
        (field === "naturalColored" && !self.visible)
      )
        return;
      const outFields = configStore.getOutfields();
      layerActions[field].run({
        ids,
        layerId: self.layerIds[0],
        enabled,
        imagery,
        view,
        outFields,
        appName: configStore.getConfig().appName,
      });
    },
  }));

const sortFeatures = sortWith([
  ascend(prop("disabled")),
  descend(prop("value")),
]);

const sortFilters = (filters) => {
  const sortedFilters = filters.map((filter) => {
    if (filter[0] && filter[0].fieldName === constants.RESOLUTION) {
      return sortWith([ascend(prop("value"))])(filter);
    } else if (filter[0] && filter[0].fieldName === constants.ACQ_YEAR_FIRST) {
      return sortWith([descend(prop("value"))])(filter);
    }
    return filter;
  });
  return sortedFilters;
};

const getFeatureCollection =
  (self, fieldName, sorter = sortFeatures) =>
  () =>
    sorter(self.features.filter((f) => f.fieldName === fieldName));

const SortedLayer = types.model({
  index: types.number,
  title: types.string,
  layerId: types.number,
});

const Selection = types.model({
  visible: types.array(types.string),
  footprintEnabled: types.array(types.string),
});

export const Features = types
  .model({
    features: types.optional(types.array(Feature), []),
    // selection: types.optional(types.array(types.string), []),
    selection: types.optional(Selection, { visible: [], footprintEnabled: [] }),
    current: types.maybe(Feature),
    visibleIds: types.array(types.number),
    extentFeatureCount: 0,
    sortedLayers: types.optional(types.array(SortedLayer), []),
  })
  .volatile((self) => ({
    maxSelection: 3,
  }))
  .actions((self) => ({
    // afterCreate() {
    //   self.loadFeatures(AppConfig.baseUrl);
    // },
    addSelection: (field, label) => {
      if (!self.selection[field]) return;
      if (
        self.selection[field].includes(label) ||
        self.selection[field].length >= 3
      )
        return;
      self.selection[field].push(label);
    },
    removeSelection: (field, label) => {
      if (!self.selection[field]) return;
      if (self.selection[field].includes(label))
        self.selection[field].remove(label);
    },
    replayAction: ({ imagery, view }) => {
      const footprintsIds = uniq(
        flatten(
          self.features.filter((f) => f.footprintEnabled).map((f) => f.layerIds)
        )
      );
      const outFields = configStore.getOutfields();
      layerActions.footprintEnabled.run({
        ids: footprintsIds,
        imagery,
        view,
        outFields,
        appName: configStore.getConfig().appName,
      });
    },
    loadFeatures: flow(function* (config) {
      let features = yield loadFeatures(config);
      let ordering = yield getFeaturesOrdering(config);
      ordering = ordering
        .filter((item) => item.constraints === "Public")
        .map((item) => ({
          index: parseInt(item.sortorder, 10),
          title: item.collectionname,
          layerId: parseInt(item.datasetid, 10),
        }));
      self.sortedLayers = ordering;

      features = features.map((feature) => {
        if (feature.fieldName === constants.DATASET_ID) {
          const id = ordering.find(
            (l) => l.layerId === feature.layerIds[0]
          ).index;
          const updated = { ...feature, id };
          return updated;
        } else return feature;
      });
      self.features = features;
    }),
    setSortedLayers: (sortedLayers) => {
      self.sortedLayers = sortedLayers;
      self.features.forEach((feature) => {
        if (feature.fieldName === constants.DATASET_ID) {
          const id = self.sortedLayers.find(
            (l) => l.layerId === feature.layerIds[0]
          ).index;
          feature.id = id;
        }
      });
    },
    setCurrent: (feature) => {
      self.current = { ...feature, layerIds: [...feature.layerIds] };
    },
    setVisibleIds: (ids) => {
      self.visibleIds = ids;
      self.disableOthers();
    },
    setExtentFeatureCount: (count) => (self.extentFeatureCount = count),
  }))
  .views((self) => ({
    selectedLayerIds: () => {
      const ids = uniq(
        flatten(self.features.filter((f) => f.selected).map((f) => f.layerIds))
      );
      return ids;
    },
    otherSelectionIds: () => {
      return uniq(
        self.features
          .filter((f) => f.selected && f.fieldName !== self.current.fieldName)
          .map((f) => [...f.layerIds])
      );
    },
  }))
  .views((self) => ({
    //
    currentFieldName: () => self.current.fieldName,
    layerIdsByField: (field) =>
      uniq(
        flatten(self.features.filter((f) => f[field]).map((f) => f.layerIds[0]))
      ),
    hasReachedMaxSelection: (field) => {
      if (!self.selection[field]) return false;
      return self.selection[field].length >= self.maxSelection;
    },
    getCurrentFieldISelectedIds: () =>
      uniq(
        flatten(
          self.features
            .filter((f) => f.selected && f.fieldName === self.current.fieldName)
            .map((f) => f.layerIds)
        )
      ),
    getOtherIds: (feature) => {
      const ids = uniq(
        flatten(
          self.features
            .filter((f) => f.selected && f.fieldName !== feature.fieldName)
            .map((f) => f.layerIds)
        )
      );
      return ids;
    },
    otherSelectedLayerIds: () => {
      const others = uniq(
        flatten(
          self.features
            .filter((f) => f.selected && f.fieldName !== self.current.fieldName)
            .map((f) => f.layerIds)
        )
      );
      const currentIds = self.current.selected
        ? [...self.current.layerIds]
        : [...self.visibleIds];
      const ids = others.length ? intersection(others, currentIds) : currentIds;
      return ids;
    },
    getSelectionIds: (currentSelection) => {
      const selectedIds = uniq(
        self.features
          .filter((f) => f.selected && f.fieldName !== self.current.fieldName)
          .map((f) => f.layerIds)
      );
      const currentIds =
        currentSelection && currentSelection.selected
          ? [...currentSelection.layerIds]
          : [...self.visibleIds];
      const ids = reduceRight(intersection, currentIds, selectedIds);
      return ids;
    },
    // attributes
    getFilters: () => {
      if (configStore.name.length > 0) {
        const filters = constants.FILTERS[configStore.name].map((filterName) =>
          getFeatureCollection(
            self,
            filterName,
            sortWith([ascend(prop("disabled")), ascend(prop("id"))])
          )()
        );
        return sortFilters(filters);
      } else return [];
    },
    datasets: getFeatureCollection(
      self,
      constants.DATASET_ID,
      sortWith([ascend(prop("disabled")), ascend(prop("id"))])
    ),
    rectification: getFeatureCollection(self, constants.RECTIFICATION),
    resolution: getFeatureCollection(self, constants.RESOLUTION),
    acqYearFirst: getFeatureCollection(self, constants.ACQ_YEAR_FIRST),
    imageDefinition: getFeatureCollection(self, constants.PRO_DEF),
  }))
  .actions((self) => ({
    disableFeatureOnExtentChange: () => {
      const visibleIds = [...self.visibleIds];
      const selectedIds = self.getSelectionIds();
      const ids = selectedIds.length
        ? intersection(selectedIds, visibleIds)
        : visibleIds;
      self.features.forEach((feature) => {
        const disabled = intersection(ids, feature.layerIds).length === 0;
        feature.update("disabled", disabled);
        if (feature.selected && disabled) feature.update("selected", false);
      });
    },
    disableOthers: () => {
      // disable other features
      const visibleIds = [...self.visibleIds];
      const features = [...self.features];
      const selectedIds = getSelectedIds({ features });
      const ids = computeIds({ visibleIds, selectedIds });
      self.features.forEach((feature) => {
        const otherIds = getOtherIds(ids, feature.fieldName);
        const disabled = !intersection(feature.layerIds, otherIds).length;
        feature.update("disabled", disabled);
        if (feature.selected && disabled) feature.update("selected", false);
      });
    },
  }))
  .actions((self) => ({
    toggle: (field, id) =>
      self.features.find((f) => f.id === id).toggle(field) && self.update(),
  }));

export const featureStore = Features.create();

if (process.env.NODE_ENV === "development")
  onSnapshot(featureStore, (newSnapshot) => {
    console.log("featureStore snapshot: ", newSnapshot);
  });
